/** Skin helpers and parameterized leftover sx. Write real `sx` at call sites. */

export const CARD_SHADOW =
  '0 0.46875rem 2.1875rem rgba(59, 62, 102, 0.03), 0 0.9375rem 1.40625rem rgba(59, 62, 102, 0.03), 0 0.25rem 0.53125rem rgba(59, 62, 102, 0.05), 0 0.125rem 0.1875rem rgba(59, 62, 102, 0.03)'
export const CARD_BORDER = '1px solid rgba(79, 80, 98, 0.3)'

export const THEME_COLORS = {
  primary: "#368cff",
  secondary: "#f8f9ff",
  success: "#1bc943",
  info: "#11c5db",
  warning: "#f4772e",
  danger: "#f83245",
  light: "#f4f5fd",
  gray: "#f8f9ff",
  dark: "#4f5062",
  first: "#4191ff",
  second: "#070919",
}

/** Nested sx selector for dark mode. Light pixels stay unchanged. */
export const ON_DARK = 'html[data-color-mode="dark"] &'

export const darkPaperBgSx = {
  [ON_DARK]: {
    backgroundColor: '#1e1e1e !important',
    background: '#1e1e1e !important',
    color: 'rgba(255, 255, 255, 0.87)',
  },
}

export const darkElevatedBgSx = {
  [ON_DARK]: {
    backgroundColor: '#2a2a2a !important',
    background: '#2a2a2a !important',
    color: 'rgba(255, 255, 255, 0.87)',
  },
}

export const darkPrimaryTextSx = {
  [ON_DARK]: {
    color: 'rgba(255, 255, 255, 0.87) !important',
  },
}

export const darkMutedTextSx = {
  [ON_DARK]: {
    color: 'rgba(255, 255, 255, 0.68) !important',
  },
}

/** Dark surfaces for Bamburgh-style list groups that hardcode #fff. */
export const darkListGroupSx = {
  'html[data-color-mode="dark"] & .MuiListItem-root, html[data-color-mode="dark"] & .MuiListItemButton-root':
    {
      backgroundColor: '#1e1e1e !important',
      borderColor: 'rgba(255, 255, 255, 0.12) !important',
      color: 'rgba(255, 255, 255, 0.87) !important',
    },
  'html[data-color-mode="dark"] & .MuiListItem-root.MuiListItem-button:hover, html[data-color-mode="dark"] & .MuiListItemButton-root:hover, html[data-color-mode="dark"] & .list-group-item-action:hover':
    {
      background: '#2a2a2a !important',
    },
  'html[data-color-mode="dark"] & .MuiListItem-root.MuiListItem-button:active, html[data-color-mode="dark"] & .MuiListItemButton-root:active, html[data-color-mode="dark"] & .list-group-item-action:active':
    {
      color: 'rgba(255, 255, 255, 0.87) !important',
      backgroundColor: '#333 !important',
    },
}

export const BRAND_COLORS = {
  facebook: "#3b5999",
  linkedin: "#0e76a8",
  discord: "#7189da",
  twitter: "#1da1f2",
  google: "#dd4b39",
  instagram: "#e4405f",
  pinterest: "#bd081c",
  youtube: "#cd201f",
  slack: "#3aaf85",
  dribbble: "#ea4c89",
  github: "#222222",
}

const YIQ_DARK = '#3b3e66'
const YIQ_LIGHT = '#fff'

function hexToRgb(hex) {
  const h = hex.replace('#', '')
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  }
}

function rgbToHex({ r, g, b }) {
  const c = (n) => Math.max(0, Math.min(255, n)).toString(16).padStart(2, '0')
  return `#${c(r)}${c(g)}${c(b)}`
}

function rgbToHsl({ r, g, b }) {
  r /= 255
  g /= 255
  b /= 255
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  let h = 0
  let s = 0
  const l = (max + min) / 2
  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0)
        break
      case g:
        h = (b - r) / d + 2
        break
      default:
        h = (r - g) / d + 4
    }
    h /= 6
  }
  return { h, s, l }
}

function hslToRgb({ h, s, l }) {
  const hue2rgb = (p, q, t) => {
    if (t < 0) t += 1
    if (t > 1) t -= 1
    if (t < 1 / 6) return p + (q - p) * 6 * t
    if (t < 1 / 2) return p
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6
    return p
  }
  let r
  let g
  let b
  if (s === 0) {
    r = g = b = l
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s
    const p = 2 * l - q
    r = hue2rgb(p, q, h + 1 / 3)
    g = hue2rgb(p, q, h)
    b = hue2rgb(p, q, h - 1 / 3)
  }
  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255),
  }
}

export function darken(hex, amount) {
  const hsl = rgbToHsl(hexToRgb(hex))
  hsl.l = Math.max(0, hsl.l - amount)
  return rgbToHex(hslToRgb(hsl))
}

export function rgba(hex, a) {
  const { r, g, b } = hexToRgb(hex)
  return `rgba(${r}, ${g}, ${b}, ${a})`
}

export function colorYiq(hex) {
  const { r, g, b } = hexToRgb(hex)
  const yiq = (r * 299 + g * 587 + b * 114) * 0.001
  return yiq >= 150 ? YIQ_DARK : YIQ_LIGHT
}

export function btnSolid(hex) {
  const text = colorYiq(hex)
  const active = darken(hex, 0.1)
  return {
    boxShadow: `0 0.25rem 0.55rem ${rgba(hex, 0.35)}`,
    color: text,
    backgroundColor: hex,
    backgroundImage: 'none',
    borderColor: hex,
    '&:not(:disabled):hover': {
      backgroundColor: hex,
      color: text,
    },
    '&:not(:disabled).active, &:not(:disabled).active:hover, &:not(:disabled):active':
      {
        backgroundColor: active,
      },
    '&:not(:disabled):active': {
      boxShadow: `0 0.22rem 0.525rem ${rgba(hex, 0.4)}, 0 0.0625rem 0.385rem ${rgba(hex, 0.54)}`,
    },
  }
}

export function btnOutline(hex) {
  const text = colorYiq(hex)
  return {
    backgroundColor: 'transparent',
    color: hex,
    border: `${hex} solid 2px`,
    '&:not(:disabled):hover': {
      boxShadow: `0 0.22rem 0.525rem ${rgba(hex, 0.4)}, 0 0.0625rem 0.385rem ${rgba(hex, 0.54)}`,
      backgroundColor: hex,
      color: text,
    },
  }
}

export function btnNeutral(hex, forceYiq) {
  const hoverText = colorYiq(hex)
  return {
    backgroundColor: rgba(hex, 0.15),
    color: forceYiq ? hoverText : hex,
    borderColor: 'transparent',
    '&:hover, &.active': {
      backgroundColor: hex,
      color: hoverText,
    },
  }
}

export function mergeSx(objects) {
  const out = {}
  for (const obj of objects) {
    if (!obj) continue
    for (const [k, v] of Object.entries(obj)) {
      if (
        v &&
        typeof v === 'object' &&
        !Array.isArray(v) &&
        out[k] &&
        typeof out[k] === 'object' &&
        !Array.isArray(out[k])
      ) {
        out[k] = { ...out[k], ...v }
      } else {
        out[k] = v
      }
    }
  }
  return out
}

export function isPlainStyle(sx) {
  return Object.keys(sx).every(
    (k) =>
      !k.startsWith('&') &&
      !k.startsWith('@') &&
      (sx[k] == null || typeof sx[k] !== 'object'),
  )
}

export function sxToStyle(sx) {
  const map = {
    m: 'margin',
    mt: 'marginTop',
    mr: 'marginRight',
    mb: 'marginBottom',
    ml: 'marginLeft',
    mx: null,
    my: null,
    p: 'padding',
    pt: 'paddingTop',
    pr: 'paddingRight',
    pb: 'paddingBottom',
    pl: 'paddingLeft',
    px: null,
    py: null,
  }
  const out = {}
  for (const [k, v] of Object.entries(sx)) {
    if (k === 'mx') {
      out.marginLeft = v
      out.marginRight = v
    } else if (k === 'my') {
      out.marginTop = v
      out.marginBottom = v
    } else if (k === 'px') {
      out.paddingLeft = v
      out.paddingRight = v
    } else if (k === 'py') {
      out.paddingTop = v
      out.paddingBottom = v
    } else {
      out[map[k] || k] = v
    }
  }
  return out
}


export function wizardStepIconSx({ active, completed }) {
  const emphasized = Boolean(active || completed)
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '0.65rem',
    flexShrink: 0,
    width: 50,
    height: 50,
    lineHeight: '50px',
    color: emphasized ? '#fff' : '#4f5062',
    backgroundColor: completed ? '#1bc943' : active ? '#368cff' : '#e6e7f1',
    boxShadow: completed
      ? '0 0.313rem 0.8rem rgba(27, 201, 67, 0.36), 0 0.126rem 0.225rem rgba(27, 201, 67, 0.18)'
      : active
        ? '0 0.313rem 0.8rem rgba(54, 140, 255, 0.36), 0 0.126rem 0.225rem rgba(54, 140, 255, 0.18)'
        : 'none',
    transition: 'all 0.2s ease-in-out',
    '& svg': { color: 'inherit' },
    ...(emphasized
      ? {}
      : {
          [ON_DARK]: {
            color: 'rgba(255, 255, 255, 0.75)',
            backgroundColor: '#3a3a3a',
          },
        }),
  }
}

export const wizardStepperTrackSx = {
  backgroundColor: '#f8f9ff',
  mb: '1rem',
  [ON_DARK]: { backgroundColor: '#252525 !important' },
}

export const wizardStepperSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'transparent',
  '& .MuiStepConnector-alternativeLabel': { top: '50%', marginTop: -18 },
  '& .MuiStepLabel-alternativeLabel': { fontSize: '1rem', color: '#4f5062' },
  '& .MuiStepLabel-label.Mui-active': { color: '#368cff', fontWeight: 700 },
  '& .MuiStepLabel-label.Mui-completed': { color: '#1bc943', fontWeight: 700 },
  [ON_DARK]: {
    '& .MuiStepLabel-alternativeLabel, & .MuiStepLabel-label': {
      color: 'rgba(255, 255, 255, 0.72)',
    },
    '& .MuiStepLabel-label.Mui-active': { color: '#90caf9', fontWeight: 700 },
    '& .MuiStepLabel-label.Mui-completed': { color: '#81c784', fontWeight: 700 },
  },
}

export const wizardPrimaryBtnSx = {
  boxShadow: '0 0.25rem 0.55rem rgba(54, 140, 255, 0.35)',
  color: '#fff',
  backgroundColor: '#368cff',
  backgroundImage: 'none',
  borderColor: '#368cff',
  fontWeight: 700,
  '&:not(:disabled):hover': { backgroundColor: '#368cff', color: '#fff' },
  '&:not(:disabled).active, &:not(:disabled).active:hover, &:not(:disabled):active':
    { backgroundColor: '#036fff' },
  '&:not(:disabled):active': {
    boxShadow:
      '0 0.22rem 0.525rem rgba(54, 140, 255, 0.4), 0 0.0625rem 0.385rem rgba(54, 140, 255, 0.54)',
  },
  [ON_DARK]: {
    color: '#fff !important',
    '&.Mui-disabled, &.MuiButton-text.Mui-disabled, &.MuiButton-contained.Mui-disabled':
      {
        backgroundColor: 'rgba(54, 140, 255, 0.42) !important',
        border: '1px solid rgba(54, 140, 255, 0.55)',
        color: 'rgba(255, 255, 255, 0.88) !important',
      },
  },
}

export function workflowAccent(name) {
  return name === 'offerToClose' ? 'success' : 'warning'
}

export function workflowCheckboxSx(name) {
  const hex = THEME_COLORS[workflowAccent(name)]
  return {
    color: hex,
    '&.Mui-checked': { color: hex },
  }
}

const searchToolbarIconButtonIdleSx = {
  boxShadow: "0 0.25rem 0.55rem rgba(54, 140, 255, 0.35)",
  color: "#fff",
  backgroundColor: "#368cff",
  backgroundImage: "none",
  borderColor: "#368cff",
  "&:not(:disabled):hover": {
    backgroundColor: "#368cff",
    color: "#fff",
  },
  "&:not(:disabled).active, &:not(:disabled).active:hover, &:not(:disabled):active": {
    backgroundColor: "#036fff",
  },
  "&:not(:disabled):active": {
    boxShadow: "0 0.22rem 0.525rem rgba(54, 140, 255, 0.4), 0 0.0625rem 0.385rem rgba(54, 140, 255, 0.54)",
  },
  "&:not(:disabled).active .btn-wrapper--icon svg path, &:hover .btn-wrapper--icon svg path": {
    fill: "#fff",
  },
  "html[data-color-mode=\"dark\"] &": {
    color: "#90caf9 !important",
    backgroundColor: "#2a2a2a !important",
    boxShadow: "none",
    "& .MuiSvgIcon-root": { color: "#90caf9 !important" },
  },
  "&:hover": {
    transform: "translateY(0px)",
  },
  height: "40px !important",
  width: "40px !important",
  lineHeight: "40px !important",
  display: "inline-flex !important",
  alignItems: "center",
  justifyContent: "center",
  p: 0,
  border: 0,
  "&:hover .btn-wrapper--icon": {
    transform: "scale(1.2)",
  },
  minWidth: "40px !important",
  "& .MuiSvgIcon-root": {
    color: "#fff",
  },
}
const searchToolbarIconButtonActiveSx = {
  boxShadow: "inset 0 0 0 2px rgba(54, 140, 255, 0.55) !important",
  color: "#368cff !important",
  backgroundColor: "#fff !important",
  backgroundImage: "none",
  borderColor: "#368cff",
  "&:not(:disabled):hover": {
    backgroundColor: "#368cff",
    color: "#fff",
  },
  "&:not(:disabled).active, &:not(:disabled).active:hover, &:not(:disabled):active": {
    backgroundColor: "#036fff",
  },
  "&:not(:disabled):active": {
    boxShadow: "0 0.22rem 0.525rem rgba(54, 140, 255, 0.4), 0 0.0625rem 0.385rem rgba(54, 140, 255, 0.54)",
  },
  "&:not(:disabled).active .btn-wrapper--icon svg path, &:hover .btn-wrapper--icon svg path": {
    fill: "#fff",
  },
  "html[data-color-mode=\"dark\"] &": {
    color: "#90caf9 !important",
    backgroundColor: "#3a3a3a !important",
    boxShadow: "inset 0 0 0 2px #368cff !important",
    "& .MuiSvgIcon-root": { color: "#90caf9 !important" },
  },
  "&:hover": {
    transform: "translateY(0px)",
    backgroundColor: "#eef4ff !important",
    color: "#368cff !important",
    "html[data-color-mode=\"dark\"] &": {
      backgroundColor: "#444 !important",
      color: "#bbdefb !important",
    },
  },
  height: "40px !important",
  width: "40px !important",
  lineHeight: "40px !important",
  display: "inline-flex !important",
  alignItems: "center",
  justifyContent: "center",
  p: 0,
  border: 0,
  "&:hover .btn-wrapper--icon": {
    transform: "scale(1.2)",
  },
  minWidth: "40px !important",
  "& .MuiSvgIcon-root": {
    color: "#368cff !important",
  },
}
export function searchToolbarIconButtonSx(active) {
  return active ? searchToolbarIconButtonActiveSx : searchToolbarIconButtonIdleSx
}

export function searchToolbarIconButtonProps(active) {
  return {
    className: active ? 'active' : undefined,
    'aria-pressed': active,
    sx: searchToolbarIconButtonSx(active),
  }
}

export const searchToolbarButtonGroupSx = {
  boxShadow: "0 0.25rem 0.55rem rgba(54, 140, 255, 0.35)",
  overflow: "hidden",
  borderRadius: "0.2rem",
  "& .MuiButton-root": {
    minWidth: "40px !important",
    width: 40,
    height: 40,
    p: 0,
    backgroundImage: "none",
    borderColor: "rgba(255,255,255,0.22) !important",
    boxShadow: "none",
  },
  "& .MuiButton-root:not(.active)": {
    color: "#fff",
    backgroundColor: "#368cff",
    "&:hover": { backgroundColor: "#368cff", color: "#fff" },
    "& .MuiSvgIcon-root": { color: "#fff" },
  },
  [ON_DARK]: {
    boxShadow: "none",
    "& .MuiButton-root:not(.active)": {
      color: "#90caf9 !important",
      backgroundColor: "#2a2a2a !important",
      "&:hover": { backgroundColor: "#333 !important", color: "#bbdefb !important" },
      "& .MuiSvgIcon-root": { color: "#90caf9 !important" },
    },
  },
}

const workflowProgressSuccessSx = {
  width: 120,
  "& .CircularProgressbar-path": {
    stroke: "#1bc943",
  },
  "& .CircularProgressbar-trail": {
    stroke: "rgba(27, 201, 67, 0.1)",
  },
  "& .CircularProgressbar-text": {
    fill: "#1bc943",
  },
  "& .CircularProgressbar-background": {
    fill: "rgba(27, 201, 67, 0.05)",
  },
}
const workflowProgressWarningSx = {
  width: 120,
  "& .CircularProgressbar-path": {
    stroke: "#f4772e",
  },
  "& .CircularProgressbar-trail": {
    stroke: "rgba(244, 119, 46, 0.1)",
  },
  "& .CircularProgressbar-text": {
    fill: "#f4772e",
  },
  "& .CircularProgressbar-background": {
    fill: "rgba(244, 119, 46, 0.05)",
  },
}
export function workflowProgressSx(name) {
  return name === 'offerToClose' ? workflowProgressSuccessSx : workflowProgressWarningSx
}

const workflowChipSuccessSx = {
  backgroundColor: "#1bc943",
  color: "#fff",
  fontWeight: 700,
}
const workflowChipWarningSx = {
  backgroundColor: "#f4772e",
  color: "#fff",
  fontWeight: 700,
}
export function workflowChipSx(name) {
  return name === 'offerToClose' ? workflowChipSuccessSx : workflowChipWarningSx
}

const propertyCardRibbonSoldSx = {
  position: "absolute",
  zIndex: 8,
  width: 160,
  height: 160,
  overflow: "hidden",
  "& > small": {
    position: "absolute",
    display: "block",
    width: "100%",
    padding: "0.25rem",
    textAlign: "center",
    textTransform: "uppercase",
    fontWeight: "bold",
    fontSize: "0.769rem",
    transform: "rotate(45deg)",
    boxShadow: "0 3px 6px -3px rgba(7, 9, 25, 0.5)",
    top: 27,
    left: 37,
    color: "#fff !important",
    backgroundColor: "#f83245 !important",
    "& .MuiTypography-root": {
      color: "#fff !important",
    },
  },
  "html[data-color-mode=\"dark\"] & > small": {
    color: "#fff !important",
    backgroundColor: "#f83245 !important",
    "& .MuiTypography-root": {
      color: "#fff !important",
    },
  },
  top: 0,
  right: 0,
}
const propertyCardRibbonContractSx = {
  position: "absolute",
  zIndex: 8,
  width: 160,
  height: 160,
  overflow: "hidden",
  "& > small": {
    position: "absolute",
    display: "block",
    width: "100%",
    padding: "0.25rem",
    textAlign: "center",
    textTransform: "uppercase",
    fontWeight: "bold",
    fontSize: "0.769rem",
    transform: "rotate(45deg)",
    boxShadow: "0 3px 6px -3px rgba(7, 9, 25, 0.5)",
    top: 27,
    left: 37,
    color: "#fff",
    backgroundColor: "#f4772e",
  },
  top: 0,
  right: 0,
}
const propertyCardRibbonPrimarySx = {
  position: "absolute",
  zIndex: 8,
  width: 160,
  height: 160,
  overflow: "hidden",
  "& > small": {
    position: "absolute",
    display: "block",
    width: "100%",
    padding: "0.25rem",
    textAlign: "center",
    textTransform: "uppercase",
    fontWeight: "bold",
    fontSize: "0.769rem",
    transform: "rotate(45deg)",
    boxShadow: "0 3px 6px -3px rgba(7, 9, 25, 0.5)",
    top: 27,
    left: 37,
    color: "#fff",
    backgroundColor: "#368cff",
  },
  top: 0,
  right: 0,
}
export function propertyCardRibbonSx(status) {
  if (status === 'Sold') return propertyCardRibbonSoldSx
  if (status === 'Under Contract') return propertyCardRibbonContractSx
  return propertyCardRibbonPrimarySx
}

const badgeToneSuccessSx = {
  display: "inline-block",
  py: 0,
  px: "0.7em",
  fontSize: "70%",
  fontWeight: 700,
  lineHeight: "21px",
  textAlign: "center",
  whiteSpace: "nowrap",
  verticalAlign: "baseline",
  borderRadius: "0.2rem",
  textTransform: "uppercase",
  height: 20,
  color: "#fff",
  backgroundColor: "#1bc943",
}
const badgeToneDangerSx = {
  display: "inline-block",
  py: 0,
  px: "0.7em",
  fontSize: "70%",
  fontWeight: 700,
  lineHeight: "21px",
  textAlign: "center",
  whiteSpace: "nowrap",
  verticalAlign: "baseline",
  borderRadius: "0.2rem",
  textTransform: "uppercase",
  height: 20,
  color: "#fff",
  backgroundColor: "#f83245",
}
const badgeTonePrimarySx = {
  display: "inline-block",
  py: 0,
  px: "0.7em",
  fontSize: "70%",
  fontWeight: 700,
  lineHeight: "21px",
  textAlign: "center",
  whiteSpace: "nowrap",
  verticalAlign: "baseline",
  borderRadius: "0.2rem",
  textTransform: "uppercase",
  height: 20,
  color: "#fff",
  backgroundColor: "#368cff",
}
const badgeToneWarningSx = {
  display: "inline-block",
  py: 0,
  px: "0.7em",
  fontSize: "70%",
  fontWeight: 700,
  lineHeight: "21px",
  textAlign: "center",
  whiteSpace: "nowrap",
  verticalAlign: "baseline",
  borderRadius: "0.2rem",
  textTransform: "uppercase",
  height: 20,
  color: "#fff",
  backgroundColor: "#f4772e",
}
const badgeToneInfoSx = {
  display: "inline-block",
  py: 0,
  px: "0.7em",
  fontSize: "70%",
  fontWeight: 700,
  lineHeight: "21px",
  textAlign: "center",
  whiteSpace: "nowrap",
  verticalAlign: "baseline",
  borderRadius: "0.2rem",
  textTransform: "uppercase",
  height: 20,
  color: "#fff",
  backgroundColor: "#11c5db",
}
const BADGE_TONES = {
  success: badgeToneSuccessSx,
  danger: badgeToneDangerSx,
  primary: badgeTonePrimarySx,
  warning: badgeToneWarningSx,
  info: badgeToneInfoSx,
}
export function badgeTokenSx(tone, extra = {}) {
  const key = String(tone || '').replace(/^badge-/, '')
  return mergeSx([BADGE_TONES[key], extra])
}

const featureCardIconBlobBaseSx = {
  height: "130px !important",
  width: "130px !important",
  lineHeight: "130px !important",
  display: "inline-flex !important",
  alignItems: "center",
  justifyContent: "center",
  p: 0,
  transition: "all 0.2s ease-in-out !important",
  transform: "scale(1) !important",
  position: "relative",
  zIndex: "0",
  "&:hover": {
    transform: "scale(1.08)",
    zIndex: 3,
  },
  border: 0,
  "@media (min-width: 1100px)": {
    transform: "scale(0.95) perspective(924px) rotateY(-8deg) rotateX(9deg) rotate(3deg) !important",
  },
  "& [data-hf=\"blob-icon-wrapper\"], & [data-hf=\"blob-wrapper\"]": {
    width: "inherit",
    height: "inherit",
    position: "absolute",
    left: 0,
    top: 0,
  },
  "& [data-hf=\"blob-icon-wrapper\"]": {
    fontSize: "1.15rem",
  },
  "& [data-hf=\"blob-icon-wrapper\"] svg": {
    height: "25%",
    width: "25%",
  },
  mx: "auto",
}
export function featureCardIconBlobSx(color) {
  return mergeSx([featureCardIconBlobBaseSx, { color }])
}

const editStrategyAddIconSidebarSx = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  height: "50px !important",
  width: "50px !important",
  lineHeight: "50px !important",
  p: 0,
  backgroundColor: "rgb(91.7692307692%, 96.4494720965%, 99.838612368%) !important",
  "html[data-color-mode=\"dark\"] &": {
    backgroundColor: "#2a2a2a !important",
  },
  color: "#4191ff",
  borderRadius: "0.65rem !important",
}
const editStrategyAddIconMainSx = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  mr: "1rem",
  height: "70px !important",
  width: "70px !important",
  lineHeight: "70px !important",
  p: 0,
  backgroundColor: "rgb(91.7692307692%, 96.4494720965%, 99.838612368%) !important",
  "html[data-color-mode=\"dark\"] &": {
    backgroundColor: "#2a2a2a !important",
  },
  color: "#4191ff",
}
export function editStrategyAddIconSx(isSidebar) {
  return isSidebar ? editStrategyAddIconSidebarSx : editStrategyAddIconMainSx
}

const reportPickerGridMenuAdminSx = {
  "& .MuiGrid-item": {
    borderRight: "1px solid #eeeff8",
    borderBottom: "1px solid #eeeff8",
    position: "relative",
  },
  "& .MuiGrid-item:nth-last-of-type(-n+1)": {
    borderBottomWidth: 0,
  },
  "& .MuiGrid-item:nth-of-type(2n)": {
    borderRightWidth: 1,
  },
  "& .MuiGrid-item:nth-last-of-type(-n+3)": {
    borderBottomWidth: 0,
  },
  "& .MuiGrid-item:nth-of-type(3n)": {
    borderRightWidth: 0,
  },
  "& .MuiGrid-item:nth-last-of-type(-n+4)": {
    borderBottomWidth: 0,
  },
  "& .MuiGrid-item:nth-of-type(4n)": {
    borderRightWidth: 0,
  },
  "& > .btn": {
    display: "block",
    border: 0,
    minWidth: "100%",
  },
}
const reportPickerGridMenuUserSx = {
  "& .MuiGrid-item": {
    borderRight: "1px solid #eeeff8",
    borderBottom: "1px solid #eeeff8",
    position: "relative",
  },
  "& .MuiGrid-item:nth-last-of-type(-n+1)": {
    borderBottomWidth: 0,
  },
  "& .MuiGrid-item:nth-of-type(2n)": {
    borderRightWidth: 1,
  },
  "& .MuiGrid-item:nth-last-of-type(-n+3)": {
    borderBottomWidth: 0,
  },
  "& .MuiGrid-item:nth-of-type(3n)": {
    borderRightWidth: 0,
  },
  "& .MuiGrid-item:nth-last-of-type(-n+4)": {
    borderBottomWidth: 0,
  },
  "& .MuiGrid-item:nth-of-type(4n)": {
    borderRightWidth: 0,
  },
  "& > .btn": {
    display: "block",
    border: 0,
    minWidth: "100%",
  },
}
export function reportPickerGridMenuSx(isAdmin) {
  return isAdmin ? reportPickerGridMenuAdminSx : reportPickerGridMenuUserSx
}

const accordionFilterToggleDarkOnSx = {
  fontWeight: 700,
  color: "rgba(255, 255, 255, 0.9) !important",
  textDecoration: "none",
  "&:hover": {
    backgroundColor: "#333 !important",
    color: "#fff !important",
    textDecoration: "underline",
    transform: "translateY(0px)",
  },
  "&:active": {
    textDecoration: "underline",
    boxShadow: "none",
  },
  "&:disabled": {
    color: "#7a7b97",
    pointerEvents: "none",
  },
  "& span": {
    position: "relative",
    "&::before": {
      content: "\"\"",
      position: "absolute",
      width: "100%",
      height: 2,
      bottom: 0,
      left: 0,
      visibility: "hidden",
      transform: "scaleX(0)",
      transition: "all 0.3s ease-in-out 0s",
    },
    transition: "all 0.2s ease-in-out",
  },
  "&:hover span::before": {
    visibility: "visible",
    transform: "scaleX(1)",
  },
  "&:disabled, &.disabled": {
    color: "#d1d2db",
    pointerEvents: "none",
  },
  "html[data-color-mode=\"dark\"] &": {
    color: "rgba(255, 255, 255, 0.95) !important",
    background: "#252525 !important",
    borderBottomColor: "rgba(255, 255, 255, 0.12) !important",
  },
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  backgroundColor: "#2a2a2a !important",
}
const accordionFilterToggleDarkOffSx = {
  fontWeight: 700,
  color: "#3b3e66",
  textDecoration: "none",
  "&:hover": {
    backgroundColor: "transparent",
    color: "#4191ff",
    textDecoration: "underline",
    transform: "translateY(0px)",
  },
  "&:active": {
    textDecoration: "underline",
    boxShadow: "none",
  },
  "&:disabled": {
    color: "#7a7b97",
    pointerEvents: "none",
  },
  "& span": {
    position: "relative",
    "&::before": {
      content: "\"\"",
      position: "absolute",
      width: "100%",
      height: 2,
      bottom: 0,
      left: 0,
      visibility: "hidden",
      transform: "scaleX(0)",
      transition: "all 0.3s ease-in-out 0s",
    },
    transition: "all 0.2s ease-in-out",
  },
  "&:hover span::before": {
    visibility: "visible",
    transform: "scaleX(1)",
  },
  "&:disabled, &.disabled": {
    color: "#d1d2db",
    pointerEvents: "none",
  },
  "html[data-color-mode=\"dark\"] &": {
    color: "rgba(255, 255, 255, 0.95) !important",
    background: "#252525 !important",
    borderBottomColor: "rgba(255, 255, 255, 0.12) !important",
  },
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
}
export function accordionFilterToggleDarkSx(isDark) {
  return isDark ? accordionFilterToggleDarkOnSx : accordionFilterToggleDarkOffSx
}

const compsSoldBadgePillSx = {
  display: "inline-block",
  py: 0,
  px: "0.7em",
  fontSize: "70%",
  fontWeight: 700,
  lineHeight: "21px",
  textAlign: "center",
  whiteSpace: "nowrap",
  verticalAlign: "baseline",
  borderRadius: 0,
  textTransform: "uppercase",
  height: 20,
  pr: "0.85em",
  pl: "0.85em",
}
export function compsSoldBadgeSx({ isPotential, soldBeforeCreated }) {
  return mergeSx([
    compsSoldBadgePillSx,
    {
      color: 'white',
      backgroundColor: soldBeforeCreated && isPotential ? '#f4772e' : '#368CFF',
    },
  ])
}

const mediaViewTabButtonOnSx = {
  backgroundColor: "#368cff",
  color: "#fff",
  border: "#368cff solid 2px",
  "&:not(:disabled):hover": {
    boxShadow: "0 0.22rem 0.525rem rgba(54, 140, 255, 0.4), 0 0.0625rem 0.385rem rgba(54, 140, 255, 0.54)",
    backgroundColor: "#368cff",
    color: "#fff",
  },
  "&:not(:disabled).active, &:hover": {
    backgroundColor: "#368cff",
    color: "#fff",
  },
  "&:hover": {
    transform: "translateY(0px)",
    backgroundColor: "#036fff",
    color: "#fff",
  },
  textTransform: "none",
  minWidth: 0,
  fontWeight: 700,
  "& .MuiSvgIcon-root, & svg": {
    color: "#fff",
  },
}
const mediaViewTabButtonOffSx = {
  backgroundColor: "transparent",
  color: "#368cff",
  border: "#368cff solid 2px",
  "&:not(:disabled):hover": {
    boxShadow: "0 0.22rem 0.525rem rgba(54, 140, 255, 0.4), 0 0.0625rem 0.385rem rgba(54, 140, 255, 0.54)",
    backgroundColor: "#368cff",
    color: "#fff",
  },
  "&:not(:disabled).active, &:hover": {
    backgroundColor: "#368cff",
    color: "#fff",
  },
  "&:hover": {
    transform: "translateY(0px)",
  },
  textTransform: "none",
  minWidth: 0,
  fontWeight: 500,
  "& .MuiSvgIcon-root, & svg": {
    color: "#368cff",
  },
}
export function mediaViewTabButtonSx(active) {
  return active ? mediaViewTabButtonOnSx : mediaViewTabButtonOffSx
}

const strategyMetricProgressOkSx = {
  height: "0.3rem !important",
  "& .MuiLinearProgress-bar::after": {
    content: "\"\"",
    opacity: 0,
    position: "absolute",
    inset: 0,
    background: "#fff",
    animation: "progress-active 2s ease infinite",
  },
  my: "0.5rem",
  background: "#f4f5fd",
  "& .MuiLinearProgress-bar": {
    background: "#1bc943",
  },
}
const strategyMetricProgressBadSx = {
  height: "0.3rem !important",
  "& .MuiLinearProgress-bar::after": {
    content: "\"\"",
    opacity: 0,
    position: "absolute",
    inset: 0,
    background: "#fff",
    animation: "progress-active 2s ease infinite",
  },
  my: "0.5rem",
  background: "#f4f5fd",
  "& .MuiLinearProgress-bar": {
    background: "#f83245",
  },
}
export function strategyMetricProgressSx(isSuccess) {
  return isSuccess ? strategyMetricProgressOkSx : strategyMetricProgressBadSx
}

const btnOutlinePrimaryTabOnSx = {
  backgroundColor: "#368cff",
  color: "#fff",
  border: "#368cff solid 2px",
  "&:not(:disabled):hover": {
    boxShadow: "0 0.22rem 0.525rem rgba(54, 140, 255, 0.4), 0 0.0625rem 0.385rem rgba(54, 140, 255, 0.54)",
    backgroundColor: "#368cff",
    color: "#fff",
  },
  "&:not(:disabled).active, &:hover": {
    backgroundColor: "#368cff",
    color: "#fff",
  },
  "&:hover": {
    transform: "translateY(0px)",
  },
}
const btnOutlinePrimaryTabOffSx = {
  backgroundColor: "transparent",
  color: "#368cff",
  border: "#368cff solid 2px",
  "&:not(:disabled):hover": {
    boxShadow: "0 0.22rem 0.525rem rgba(54, 140, 255, 0.4), 0 0.0625rem 0.385rem rgba(54, 140, 255, 0.54)",
    backgroundColor: "#368cff",
    color: "#fff",
  },
  "&:not(:disabled).active, &:hover": {
    backgroundColor: "#368cff",
    color: "#fff",
  },
  "&:hover": {
    transform: "translateY(0px)",
  },
}
export function btnOutlinePrimaryTabSx(active) {
  return active ? btnOutlinePrimaryTabOnSx : btnOutlinePrimaryTabOffSx
}

const reportTileButtonSelectedSx = {
  backgroundColor: "#f8f9ff",
  color: "#3b3e66",
  border: 0,
  "&:not(:disabled):hover": {
    boxShadow: "0 0.22rem 0.525rem rgba(248, 249, 255, 0.4), 0 0.0625rem 0.385rem rgba(248, 249, 255, 0.54)",
    backgroundColor: "#f8f9ff",
    color: "#3b3e66",
  },
  "&:not(:disabled).active, &:hover": {
    backgroundColor: "#f8f9ff",
    color: "#3b3e66",
  },
  "html[data-color-mode=\"dark\"] &": {
    color: "rgba(255, 255, 255, 0.87)",
    backgroundColor: "rgba(255, 255, 255, 0.1) !important",
  },
  "&:hover": {
    transform: "translateY(0px)",
  },
  width: "100%",
  display: "block",
  borderRadius: 0,
  boxShadow: "none",
  py: "1.5rem",
  px: "0.25rem",
  textAlign: "center",
}
const reportTileButtonIdleSx = {
  backgroundColor: "#f8f9ff",
  color: "#3b3e66",
  border: 0,
  "&:not(:disabled):hover": {
    boxShadow: "0 0.22rem 0.525rem rgba(248, 249, 255, 0.4), 0 0.0625rem 0.385rem rgba(248, 249, 255, 0.54)",
    backgroundColor: "#f8f9ff",
    color: "#3b3e66",
  },
  "&:not(:disabled).active, &:hover": {
    backgroundColor: "#f8f9ff",
    color: "#3b3e66",
  },
  "html[data-color-mode=\"dark\"] &": {
    color: "rgba(255, 255, 255, 0.87)",
  },
  "&:hover": {
    transform: "translateY(0px)",
  },
  width: "100%",
  display: "block",
  borderRadius: 0,
  boxShadow: "none",
  py: "1.5rem",
  px: "0.25rem",
  textAlign: "center",
}
export function reportTileButtonSx(isSelected) {
  return isSelected ? reportTileButtonSelectedSx : reportTileButtonIdleSx
}

export function trialLabelTextSx(trialLabel) {
  if (trialLabel.startsWith('Ended')) return { color: "#a6a6b9" }
  if (trialLabel.includes('–') || trialLabel.startsWith('Ends')) {
    return { color: "#368cff" }
  }
  return undefined
}
